import React, { useEffect } from "react";
import "./FAQ.css";

const PAGE_TITLE = "Frequently Asked Questions";
const PAGE_SUBTITLE = "Have questions? Find instant answers or reach out directly to our team.";
const TOC = [
  "Global Vision, Ecosystem & the .weave Format (Questions 1 to 5)",
  "Governance, Actors & RACI Access Control Matrix (Questions 6 to 9)",
  "Authentication, Onboarding & Session Security (Questions 10 to 14)",
  "Home Feed, Discovery & AI Recommendation Engine (Questions 15 to 18)",
  "Real-Time Messaging & Intelligent Notifications Hub (Questions 19 to 22)",
  "User Profile, Multi-Tenant Organizations & Settings (Questions 23 to 27)",
  "Media Portfolio & Multimodal AI Ingestion Gate (Content Guard) (Questions 28 to 32)",
  "Weave Wizard — 3-Step Project Blueprinting (Questions 33 to 37)",
  "The 6 Proprietary Narrative Layouts (Questions 38 to 41)",
  "Contribution Engine & 'As Creator' Management (Questions 42 to 45)",
  "Weave Studio, Automated Assembly & Post-Production Export (Questions 46 to 48)",
  "Economic Model, Creator Wallet & Smart Escrow Payouts (Questions 49 to 50)"
];
const SECTIONS = [
  {
    "title": "GLOBAL VISION, ECOSYSTEM & THE .WEAVE FORMAT",
    "items": [
      {
        "type": "qa",
        "q": "Q1: What is Weavevo and how does it reinvent video production?",
        "a": "A: Weavevo is an AI-orchestrated collaborative media creation platform. It replaces static, isolated video files with a dynamic narrative entity: “The weave”, partitioned into structured filming assignments (\"Slots\") distributed to a community of field creators (\"Weavers\")."
      },
      {
        "type": "qa",
        "q": "Q2: What is the concept of a \"Weave\" and \"Zero Obsolescence\"?",
        "a": "A: A Weave lives on a persistent, universal URL. New footage can be hot-swapped into existing slots without regenerating embed codes or breaking viewer links, maintaining narrative history through immutable versioned snapshots."
      },
      {
        "type": "qa",
        "q": "Q3: What is the operational duality between Weavevo Mobile App and Weavevo Web Studio?",
        "a": "A: The Flutter Mobile App (iOS/Android) powers field operations (discovery, video/photo capture, quick slot reservation, direct messaging, and mobile Wallet). The Web Studio is a browser-based SaaS suite for creative directors, handling advanced multi-track editing, subtitle styling, and 4K master rendering. Weavevo Web Studio will be delivered in March 2027."
      },
      {
        "type": "qa",
        "q": "Q4: What is the proprietary .weave narrative container format?",
        "a": "A: The .weave file is a standardized modular container encapsulating the narrative blueprint JSON, raw media asset pointers, isolated audio stems (AI voice-over, adaptive MusicGen score, ambient audio), cryptographic provenance hashes, and the escrow royalty registry."
      },
      {
        "type": "qa",
        "q": "Q5: How are Weavevo's brand and technology legally protected?",
        "a": "Weavevo est une marque déposée et une technologie brevetée déposée auprès de l'INPI (France), avec une extension prioritaire actuellement en cours auprès de l'USPTO (États-Unis), couvrant la Classe 9 (logiciels et IA), la Classe 38 (télécommunications et streaming) et la Classe 41 (production audiovisuelle collaborative)."
      }
    ]
  },
  {
    "title": "GOVERNANCE, ACTORS & RACI ACCESS CONTROL MATRIX",
    "items": [
      {
        "type": "qa",
        "q": "Q6: What are the 5 user roles recognized on the Weavevo platform?",
        "a": "A: Viewer (public consumption), Weaver (field contributor completing slots), Co-Creator (delegated collaborator with editing and moderation rights), Creator (Weave owner and escrow budget guarantor), Organization Manager (manages the enterprise workspace, team creators working for the company, permissions, and brand governance)."
      },
      {
        "type": "qa",
        "q": "Q7: What are the core permissions of a \"Weaver\" (Contributor)?",
        "a": "A: A Weaver browses open slots via geolocated maps or discovery feeds, locks time slots, captures and uploads raw media footage, and collects automated escrow bounty disbursements directly into their personal Wallet."
      },
      {
        "type": "qa",
        "q": "Q8: What project capabilities are granted to a \"Co-Creator\"?",
        "a": "A: A Co-Creator can modify the narrative blueprint, moderate inbound community submissions, select candidate takes for each slot, and participate in timeline assembly inside the Studio."
      },
      {
        "type": "qa",
        "q": "Q9: What exclusive powers are reserved solely for the \"Creator\"?",
        "a": "A: Only the Creator can fund the escrow vault, execute final approval on completed slots, trigger automated bounty payouts to Weavers, and authorize master publication across public channels."
      }
    ]
  },
  {
    "title": "AUTHENTICATION, ONBOARDING & SESSION SECURITY",
    "items": [
      {
        "type": "qa",
        "q": "Q10: What technologies secure user authentication and API sessions?",
        "a": "A: Authentication is centralized via Keycloak OIDC/OAuth2, issuing RSA256-signed JWT Access Tokens (15-minute lifespan), rotating Refresh Tokens in secure HttpOnly storage, TLS 1.3 encryption, and Argon2id/bcrypt credential hashing."
      },
      {
        "type": "qa",
        "q": "Q11: Which login methods are supported on the mobile application?",
        "a": "A: Fast single-tap Google SSO (OAuth 2.0 federation) and standard email/username (@handle) login with credential toggles for visibility inspection."
      },
      {
        "type": "qa",
        "q": "Q12: What password complexity rules are enforced?",
        "a": "A: Passwords must contain a minimum of 8 characters, at least 1 uppercase letter, 1 numeric digit, 1 special character, and must strictly match the verification field."
      },
      {
        "type": "qa",
        "q": "Q13: How does the system defend against brute-force attacks?",
        "a": "A: The authentication gateway triggers a mandatory 15-minute account lock after 5 consecutive failed authentication attempts from the same account identifier or client IP address."
      },
      {
        "type": "qa",
        "q": "Q14: What is the validity duration of the password reset OTP code?",
        "a": "A: The 6-digit security OTP sent via Resend transactional email expires strictly after 15 minutes of inactivity."
      }
    ]
  },
  {
    "title": "HOME FEED, DISCOVERY & AI RECOMMENDATION ENGINE",
    "items": [
      {
        "type": "qa",
        "q": "Q15: How is the bottom navigation bar (Bottom Nav) structured?",
        "a": "A: It features 6 primary destinations: Home Feed, Direct Messages, Central '+' (Weave Creation Wizard), Portfolio Grid (4 squares), Wallet, and User Profile Avatar."
      },
      {
        "type": "qa",
        "q": "Q16: Which status filters organize the main project feed?",
        "a": "A: Three pill filters: 'All' (complete catalog), 'In the loom' (active weave currently seeking media contributions), and 'Woven' (finalized, published video productions)."
      },
      {
        "type": "qa",
        "q": "Q17: How does the \"Suggested Weaves\" recommendation carousel operate?",
        "a": "A: A hybrid retrieval engine combining Qdrant vector search and 768-dimensional Gemini embeddings computes cosine similarities against user interest vectors, presenting cards with match badges (e.g., \"✨ Top match 100%\") and a \"🔄 New ideas\" refresh trigger."
      },
      {
        "type": "qa",
        "q": "Q18: What happens when a user dismisses a recommendation with '✕'?",
        "a": "A: Clicking '✕' immediately removes the card from the view and records an explicit negative feedback signal, dynamically updating the user's vector weights to refine subsequent suggestions."
      }
    ]
  },
  {
    "title": "REAL-TIME MESSAGING & INTELLIGENT NOTIFICATIONS HUB",
    "items": [
      {
        "type": "qa",
        "q": "Q19: What protocol drives in-app collaboration and instant messaging?",
        "a": "A: Low-latency bidirectional WebSockets (running on service port :3003) manage real-time text exchange, typing indicators, delivery receipts, and emoji reactions (❤️)."
      },
      {
        "type": "qa",
        "q": "Q20: How does the messaging engine prevent duplicate conversation threads (RG-MSG-001)?",
        "a": "A: Selecting an existing direct contact from the \"New message\" modal automatically redirects to the active 1-to-1 conversation thread without creating redundant database records."
      },
      {
        "type": "qa",
        "q": "Q21: What are the 4 specialized streams of the Notifications Hub?",
        "a": "A: 'Local Threads' (proximity filming calls), 'AI Suggests' (creative concept prompts), 'Approvals' (rush review alerts), and 'Pulse' (community social interactions)."
      },
      {
        "type": "qa",
        "q": "Q22: What is the geographical trigger radius for \"Local Threads\" notifications?",
        "a": "A: A Local Threads notification is triggered only when a Weaver is within a default 25 km radius of the slot's Point of Interest (POI). This detection radius can be customized at any time in the user profile settings."
      }
    ]
  },
  {
    "title": "USER PROFILE, MULTI-TENANT ORGANIZATIONS & SETTINGS",
    "items": [
      {
        "type": "qa",
        "q": "Q23: What essential elements are displayed on the main Creator Profile?",
        "a": "A: The user avatar, display name, unique @username, active organization branding banner, and the 6-KPI \"Your Profile Pulse\" dashboard summarizing creative output."
      },
      {
        "type": "qa",
        "q": "Q24: What are the minimum requirements to qualify for the Gold Creator Tier?",
        "a": "A: Creators must achieve a minimum of 10 fully approved Weaves and maintain an audience engagement rate above 15% to unlock the \"Request Gold\" tier upgrade."
      },
      {
        "type": "qa",
        "q": "Q25: How does multi-tenant organization switching operate?",
        "a": "A: Through the \"Change Organization\" switcher, users toggle between enterprise workspaces (e.g., Le Louvre, Century 21) and their personal profile, enforcing strict data isolation across media and projects."
      },
      {
        "type": "qa",
        "q": "Q26: Why is Dark Mode locked and unavailable on mobile?",
        "a": "A: The mobile client enforces an unalterable light mode (\"Disabled: Weavevo mobile always stays in light mode\") to ensure consistent color grading and luminance calibration across video assets."
      },
      {
        "type": "qa",
        "q": "Q27: What capabilities are unlocked by toggling a \"Professional Account\"?",
        "a": "A: A Professional Account grants access to integrated promotional campaigns, detailed audience retention graphs, and sponsored slot bounties."
      }
    ]
  },
  {
    "title": "MEDIA PORTFOLIO & MULTIMODAL AI INGESTION GATE (CONTENT GUARD)",
    "items": [
      {
        "type": "qa",
        "q": "Q28: What are the three functional workspaces inside the Portfolio?",
        "a": "A: 'As Creator' (owned projects), 'As Weaver' (completed assignments and missions), and 'My Media' (raw personal media vault)."
      },
      {
        "type": "qa",
        "q": "Q29: What file size limits and batch quotas govern media uploads?",
        "a": "A: Uploads are capped at 10 MB for still photos and 200 MB for video files, with a maximum processing batch of 12 simultaneous items in the ingestion queue."
      },
      {
        "type": "qa",
        "q": "Q30: Can a raw media file be attached to a Weave without passing AI screening?",
        "a": "A: No. Every asset must successfully pass the multimodal AI Content Guard (Gemini Vision + FFmpeg keyframe analysis) before becoming eligible for slot binding or public sharing."
      },
      {
        "type": "qa",
        "q": "Q31: What is the maximum acceptable risk threshold for automated approval?",
        "a": "A: Any upload with an AI-calculated Risk Score exceeding 15% is immediately quarantined from automated publishing and diverted to Level-2 manual moderation."
      },
      {
        "type": "qa",
        "q": "Q32: How does Weavevo prevent plagiarism and duplicate submissions?",
        "a": "A: The Trust Scoring Engine computes perceptual image hashes (pHash) and validates EXIF camera metadata to detect reused, cloned, or downloaded stock media."
      }
    ]
  },
  {
    "title": "WEAVE WIZARD — 3-STEP PROJECT BLUEPRINTING",
    "items": [
      {
        "type": "qa",
        "q": "Q33: What are the 3 sequential steps of the Weave Wizard?",
        "a": "A: Step 1: Blueprint (Title, context description, cover art, tags, layout selection); Step 2: Slots (scene structuring, framing directives, timing, budget); Step 3: Settings (workspace privacy and team permissions)."
      },
      {
        "type": "qa",
        "q": "Q34: How is project cover artwork configured in Step 1?",
        "a": "A: Creators can either upload local image files ([ Upload ]) or generate a photorealistic cover via Google Imagen 4.0 ([ Generate AI ]) based on the weave brief."
      },
      {
        "type": "qa",
        "q": "Q35: What criteria validate Step 1 Blueprint?",
        "a": "A: A title containing 5 to 100 characters, a narrative description, and the mandatory selection of one of the 6 proprietary layout structures."
      },
      {
        "type": "qa",
        "q": "Q36: How does Gemini 2.0 Flash assist slot generation in Step 2?",
        "a": "A: Toggling 'Use AI' allows creators to enter a high-level filming concept; Gemini automatically drafts structured JSON scenes containing titles, framing prompts, and target durations."
      },
      {
        "type": "qa",
        "q": "Q37: What technical attributes define a single Slot specification?",
        "a": "A: An explicit slot title, a narrative framing directive (Mission), an optional Google Places geolocation anchor, and a target duration of 3 to 60 seconds (standard 5 to 8s)."
      }
    ]
  },
  {
    "title": "THE 6 PROPRIETARY NARRATIVE LAYOUTS",
    "items": [
      {
        "type": "qa",
        "q": "Q38: What defines the \"Storyboard\" narrative layout?",
        "a": "A: Storyboard enforces a strict, chronological sequence (Beginning, Middle, End) where each slot represents a non-negotiable scene in the narrative chain."
      },
      {
        "type": "qa",
        "q": "Q39: How does the \"Gallery\" layout function?",
        "a": "A: Gallery offers an open, mosaic-style collection framework without chronological constraints, ideal for capturing multi-angle impressions of open events."
      },
      {
        "type": "qa",
        "q": "Q40: What are the objectives of the \"Seed\" and \"Symphony\" layouts?",
        "a": "A: 'Seed' broadcasts a thematic creative prompt open to diverse creator interpretations. 'Symphony' synchronizes an acoustic, rhythm-driven sequence emphasizing ambient audio and musical beats."
      },
      {
        "type": "qa",
        "q": "Q41: How do \"Relay\" and \"Versus\" layouts structure visual storytelling?",
        "a": "A: 'Relay' creates a visual baton pass where kinetic motion from one shot carries into the next. 'Versus' employs a synchronized 50/50 split-screen comparing dual perspectives side-by-side."
      },
      {
        "type": "note",
        "text": "For more details: https://weavevo.com/templates"
      }
    ]
  },
  {
    "title": "CONTRIBUTION ENGINE & 'AS CREATOR' MANAGEMENT",
    "items": [
      {
        "type": "qa",
        "q": "Q42: How does a Weaver locate relevant Weaves to contribute to?",
        "a": "A: Through the 'Contribute to a weave' hub, offering two directories: 'Weaves explorer' (all open community missions) and 'Suggested for you' (geolocated, AI-curated matches)."
      },
      {
        "type": "qa",
        "q": "Q43: How does the AI evaluate and rank submissions received for a slot?",
        "a": "A: The Studio AI Reviewer assesses submitted video frames against the slot's framing mission, scoring contextual relevance (Fit Score, e.g., \"Fit 86%\") and technical stability (\"Good quality\"), automatically tagging the top take as \"AI selected\" to guide the creator's choice."
      },
      {
        "type": "qa",
        "q": "Q44: What controls are provided in the 'As Creator' (Open the Loom) cockpit?",
        "a": "A: Creators can navigate sequentially across all project slots, inspect incoming candidate submissions, evaluate AI Fit Scores, trigger quality rescans, and designate winning takes.]"
      },
      {
        "type": "qa",
        "q": "Q45: How can a project owner fill an unassigned or vacant slot?",
        "a": "A: Using the primary [ Contribute to this slot ] button, the creator can bypass community submission by importing an existing file from their vault or launching the in-app camera."
      }
    ]
  },
  {
    "title": "WEAVE STUDIO, AUTOMATED ASSEMBLY & POST-PRODUCTION EXPORT",
    "items": [
      {
        "type": "qa",
        "q": "Q46: Which automated transition styles does Weave Studio support (Editing Rhythm)?",
        "a": "A: Many automated transition engines: 'Cut' (hard cut, default), 'Fade' (cross-dissolve), 'Slide' (directional push), 'Zoom' (kinetic scaling), and 'Wipe' (linear wipe), .."
      },
      {
        "type": "qa",
        "q": "Q47: What intelligent audio mastering options are available?",
        "a": "A: Multilingual text-to-speech narration via Google Cloud TTS (FR, EN, AR), custom audio stems, and built-in music presets (Trending Pop, Lo-fi Chill, Cinematic, Afro Groove) with automated ducking."
      },
      {
        "type": "qa",
        "q": "Q48: What output formats and visual templates are generated at render?",
        "a": "A: Output options include the native .weave master, horizontal 16:9 videos in 720p/1080p Master (4K on Web Studio), and vertical 9:16 cuts tailored for social platforms, formatted via templates like Classic Sequential or Grid 2x2."
      }
    ]
  },
  {
    "title": "ECONOMIC MODEL, CREATOR WALLET & SMART ESCROW PAYOUTS",
    "items": [
      {
        "type": "qa",
        "q": "Q49: How does the Smart Escrow financial mechanism operate?",
        "a": "A: When a funded Weave is launched, creator funds are locked into an escrow contract. As soon as a Weaver's contribution is locked into the final cut, rewards are automatically disbursed to their Wallet."
      },
      {
        "type": "qa",
        "q": "Q50: How do creators track project earnings and withdraw funds?",
        "a": "A: Users review net earnings filtered by role ('As Creator' vs 'As Weaver'), inspect per-project financial performance via the \"Weave's Wallet\" dashboard (Pool breakdown and item shares), and initiate direct PayPal transfers."
      },
      {
        "type": "qa",
        "q": "Q51: Does the Weaver continue to earn revenue once the Weave is published and monetized on YouTube?",
        "a": "A: Yes. Beyond the initial bounty released upon final cut (Woven status), the Weaver earns recurring royalties from YouTube ad revenue for as long as the video remains online. The automated split engine calculates each contributor's share based on their prorated percentage of the project's master pool (e.g., 33.3%)."
      },
      {
        "type": "qa",
        "q": "Q52: How are recurring YouTube earnings tracked and credited to the Weaver's Wallet?",
        "a": "A: View metrics and YouTube ad earnings are synchronized via the YouTube API and tracked on the \"Weave earning\" screen. Pending amounts appear first under \"Coming\" balances before being posted to the Wallet's \"Received total\", withdrawable via PayPal once the €10 threshold is met."
      }
    ]
  }
];

const sectionId = (index) => `faq-section-${index + 1}`;

export default function FAQ() {
  useEffect(() => {
    document.title = `${PAGE_TITLE} | Weavevo`;
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="faq-docPage">
      <section className="faq-toc" aria-labelledby="faq-toc-title">
        <h1 id="faq-toc-title">{PAGE_TITLE}</h1>
        <p className="faq-subtitle">{PAGE_SUBTITLE}</p>
        <nav className="faq-tocGrid" aria-label={PAGE_TITLE}>
          {TOC.map((item, index) => (
            <a key={item} href={`#${sectionId(index)}`}>{item}</a>
          ))}
        </nav>
      </section>

      <div className="faq-sections">
        {SECTIONS.map((section, sectionIndex) => (
          <section className="faq-section" id={sectionId(sectionIndex)} key={section.title}>
            <h2>{section.title}</h2>
            <div className="faq-questions">
              {section.items.map((item, itemIndex) => {
                if (item.type === "note") {
                  const match = item.text.match(/^(For more details:)\s+(https?:\/\/\S+)$/);
                  return match ? (
                    <p className="faq-note" key={`${section.title}-note-${itemIndex}`}>
                      {match[1]} <a href={match[2]}>{match[2]}</a>
                    </p>
                  ) : (
                    <p className="faq-note" key={`${section.title}-note-${itemIndex}`}>{item.text}</p>
                  );
                }

                return (
                  <details className="faq-item" key={item.q}>
                    <summary>
                      <span>{item.q}</span>
                      <span className="faq-chevron" aria-hidden="true" />
                    </summary>
                    <div className="faq-answer">{item.a}</div>
                  </details>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </main>
  );
}
